import { Action, Dispatch } from 'redux';

import { CONTACTS_URL, TICKETS_URL } from 'appConstants';
import {
  AccountUtils,
  ApiRequest,
  FeatureFlags,
  RecipientUtils,
  TicketUtils,
} from 'services';
import {
  CloudGroupUser,
  CommentResponse,
  ContactsResource,
  Recipient,
  TicketResource,
} from 'types';
import {
  AppState,
  setCurrentRecipients,
  setProcessingFlag,
  setRecipientsUpdated,
  startPolling,
} from 'reducers';

/* Types */

export interface RecipientsAction extends Action {
  readonly data?: Recipient[];
  readonly email?: string;
  readonly filterText?: string;
  readonly id?: string;
  readonly isValid?: boolean;
  readonly recipient?: Recipient;
  readonly rpn?: string;
  readonly unverifiedId?: string;
}

export interface Recipients {
  readonly _errorListItems: boolean;
  readonly _errorAddUsers: boolean;
  readonly _loadingListItems: boolean;
  readonly _loadingAddUsers: boolean;
  readonly addUserIsVisible: boolean;
  readonly addUsersItems: Recipient[];
  readonly recipientsFilterText: string;
  readonly usersFilterText: string;
}

/* Initial State */

const initialState: Recipients = {
  _errorListItems: false,
  _errorAddUsers: false,
  _loadingListItems: false,
  _loadingAddUsers: false,
  addUserIsVisible: false,
  addUsersItems: [],
  recipientsFilterText: '',
  usersFilterText: '',
};

/* Actions */

enum Actions {
  ADD_NEW_UNVERIFIED_INPUT = 'ADD_NEW_UNVERIFIED_INPUT',
  ADD_RECIPIENTS_ERROR = 'ADD_RECIPIENTS_ERROR',
  ADD_RECIPIENTS_START = 'ADD_RECIPIENTS_START',
  ADD_RECIPIENTS_SUCCESS = 'ADD_RECIPIENTS_SUCCESS',
  CLEAR_ALL_UNVERIFIED_INPUTS = 'CLEAR_ALL_UNVERIFIED_INPUTS',
  CLEAR_ALL_USER_CHECKS = 'CLEAR_ALL_USER_CHECKS',
  GET_USERS_ERROR = 'GET_USERS_ERROR',
  GET_USERS_START = 'GET_USERS_START',
  GET_USERS_SUCCESS = 'GET_USERS_SUCCESS',
  HIDE_ADD_USER_POPOVER = 'HIDE_ADD_USER_POPOVER',
  REMOVE_NEW_UNVERIFIED_INPUT = 'REMOVE_NEW_UNVERIFIED_INPUT',
  REMOVE_RECIPIENTS_ERROR = 'REMOVE_RECIPIENTS_ERROR',
  REMOVE_RECIPIENTS_START = 'REMOVE_RECIPIENTS_START',
  REMOVE_RECIPIENTS_SUCCESS = 'REMOVE_RECIPIENTS_SUCCESS',
  RECIPIENTS_LOADING = 'RECIPIENTS_LOADING',
  RECIPIENTS_SUCCESS = 'RECIPIENTS_SUCCESS',
  RECIPIENTS_ERROR = 'RECIPIENTS_ERROR',
  RESET_ADD_USER = 'RESET_ADD_USER',
  SET_RECIPIENTS_FILTER = 'SET_RECIPIENTS_FILTER',
  SET_USERS_FILTER = 'SET_USERS_FILTER',
  SHOW_ADD_USER_POPOVER = 'SHOW_ADD_USER_POPOVER',
  TOGGLE_USER_CHECK = 'TOGGLE_USER_CHECK',
  UPDATE_EMAIL_INPUT = 'UPDATE_EMAIL_INPUT',
}

const addNewUnverifiedInput = (): RecipientsAction => ({
  type: Actions.ADD_NEW_UNVERIFIED_INPUT,
});

const clearAllUnverifiedInputs = (): RecipientsAction => ({
  type: Actions.CLEAR_ALL_UNVERIFIED_INPUTS,
});

const clearAllUserChecks = (): RecipientsAction => ({
  type: Actions.CLEAR_ALL_USER_CHECKS,
});

const getUsersError = (): RecipientsAction => ({
  type: Actions.GET_USERS_ERROR,
});

const getUsersStart = (): RecipientsAction => ({
  type: Actions.GET_USERS_START,
});

const getUsersSuccess = (data: Recipient[]): RecipientsAction => ({
  type: Actions.GET_USERS_SUCCESS,
  data,
});

const removeNewUnverified = (unverifiedId: string): RecipientsAction => ({
  type: Actions.REMOVE_NEW_UNVERIFIED_INPUT,
  unverifiedId,
});

const removeRecipientsError = () => ({
  type: Actions.REMOVE_RECIPIENTS_ERROR,
});

const removeRecipientsSuccess = (): RecipientsAction => ({
  type: Actions.REMOVE_RECIPIENTS_SUCCESS,
});

const resetAddUser = (): RecipientsAction => ({
  type: Actions.RESET_ADD_USER,
});

const setAddUserVisibility = (visibility: boolean): RecipientsAction => ({
  type: visibility
    ? Actions.SHOW_ADD_USER_POPOVER
    : Actions.HIDE_ADD_USER_POPOVER,
});

const recipientsSuccess = (): RecipientsAction => ({
  type: Actions.RECIPIENTS_SUCCESS,
});
const addRecipientsSuccess = (): RecipientsAction => ({
  type: Actions.ADD_RECIPIENTS_SUCCESS,
});

const recipientsLoading = (): RecipientsAction => ({
  type: Actions.RECIPIENTS_LOADING,
});

const addRecipientStart = (): RecipientsAction => ({
  type: Actions.ADD_RECIPIENTS_START,
});

const removeRecipientsStart = (): RecipientsAction => ({
  type: Actions.REMOVE_RECIPIENTS_START,
});

const recipientsError = (): RecipientsAction => ({
  type: Actions.RECIPIENTS_ERROR,
});

const addRecipientsError = (): RecipientsAction => ({
  type: Actions.ADD_RECIPIENTS_ERROR,
});

const setRecipientsFilterText = (filterText: string): RecipientsAction => ({
  type: Actions.SET_RECIPIENTS_FILTER,
  filterText,
});

const setUsersFilterText = (filterText: string): RecipientsAction => ({
  type: Actions.SET_USERS_FILTER,
  filterText,
});

const toggleUserCheck = (id: string): RecipientsAction => ({
  type: Actions.TOGGLE_USER_CHECK,
  id,
});

const updateEmailInput = (
  unverifiedId: string,
  email: string,
  isValid: boolean,
): RecipientsAction => ({
  type: Actions.UPDATE_EMAIL_INPUT,
  email,
  unverifiedId,
  isValid,
});

/* Reducer */

const recipientsReducer = (state = initialState, action: RecipientsAction) => {
  switch (action.type) {
    case Actions.ADD_NEW_UNVERIFIED_INPUT:
      const newId = () => `${new Date().getTime()}`;
      const newUnverified = {
        _isVisible: true,
        _isChecked: false,
        _isPrimary: false,
        _toRemove: false,
        _unverifiedId: newId,
        roles: [],
        email: '',
        fullName: '',
        id: '',
        username: '',
      };

      return {
        ...state,
        addUsersItems: [...state.addUsersItems, newUnverified],
      };
    case Actions.CLEAR_ALL_UNVERIFIED_INPUTS:
      return {
        ...state,
        addUsersItems: state.addUsersItems.filter(
          (user) => !user._unverifiedId,
        ),
      };
    case Actions.CLEAR_ALL_USER_CHECKS:
      return {
        ...state,
        addUsersItems: state.addUsersItems.map((user: Recipient) => {
          return {
            ...user,
            _isChecked: false,
          };
        }),
      };
    case Actions.GET_USERS_ERROR:
      return {
        ...state,
        _errorAddUsers: true,
        _loadingAddUsers: false,
      };
    case Actions.GET_USERS_START:
      return {
        ...state,
        _errorAddUsers: false,
        _loadingAddUsers: true,
      };
    case Actions.GET_USERS_SUCCESS:
      return {
        ...state,
        _errorAddUsers: false,
        _loadingAddUsers: false,
        addUsersItems: action.data,
      };
    case Actions.REMOVE_NEW_UNVERIFIED_INPUT:
      return {
        ...state,
        addUsersItems: state.addUsersItems.map((user) => {
          const noUnverifiedId = !user._unverifiedId;
          const notSelectedItem = user._unverifiedId !== action.unverifiedId;

          if (noUnverifiedId || notSelectedItem) {
            return user;
          }

          return {
            ...user,
            _isVisible: false,
            _isChecked: false,
          };
        }),
      };
    case Actions.RESET_ADD_USER:
      return {
        ...state,
        addUsersItems: state.addUsersItems
          .map((user: Recipient) => {
            return {
              ...user,
              _isChecked: false,
            };
          })
          .filter((user: Recipient) => !user._unverifiedId),
        addUserIsVisible: false,
        usersFilterText: '',
      };
    case Actions.SHOW_ADD_USER_POPOVER:
      return {
        ...state,
        addUserIsVisible: true,
        usersFilterText: '',
      };
    case Actions.HIDE_ADD_USER_POPOVER:
      return {
        ...state,
        addUserIsVisible: false,
        usersFilterText: '',
      };
    case Actions.ADD_RECIPIENTS_SUCCESS:
    case Actions.RECIPIENTS_SUCCESS:
    case Actions.REMOVE_RECIPIENTS_SUCCESS:
      return {
        ...state,
        _errorListItems: false,
        _loadingListItems: false,
      };
    case Actions.ADD_RECIPIENTS_START:
    case Actions.RECIPIENTS_LOADING:
    case Actions.REMOVE_RECIPIENTS_START:
      return {
        ...state,
        _errorListItems: false,
        _loadingListItems: true,
      };
    case Actions.ADD_RECIPIENTS_ERROR:
    case Actions.RECIPIENTS_ERROR:
    case Actions.REMOVE_RECIPIENTS_ERROR:
      return {
        ...state,
        _errorListItems: true,
        _loadingListItems: false,
      };
    case Actions.SET_RECIPIENTS_FILTER:
      return {
        ...state,
        recipientsFilterText: action.filterText,
      };
    case Actions.SET_USERS_FILTER:
      return {
        ...state,
        usersFilterText: action.filterText,
      };
    case Actions.TOGGLE_USER_CHECK:
      return {
        ...state,
        addUsersItems: state.addUsersItems.map((user: Recipient) => {
          if (user.id !== action.id) {
            return user;
          }

          return {
            ...user,
            _isChecked: !user._isChecked,
          };
        }),
      };
    case Actions.UPDATE_EMAIL_INPUT:
      return {
        ...state,
        addUsersItems: state.addUsersItems.map((user) => {
          const noUnverifiedId = !user._unverifiedId;
          const notSelectedItem = user._unverifiedId !== action.unverifiedId;
          const isInputEmpty = action.email === '';

          if (noUnverifiedId || notSelectedItem) {
            return { ...user };
          }
          return {
            ...user,
            _hasValidEmail: action.isValid,
            _isChecked: isInputEmpty ? false : action.isValid,
            email: action.email,
            fullName: action.email,
            id: action.email,
          };
        }),
      };
    default:
      return state;
  }
};

/* Thunks */

const getUsers = (
  accountID: string,
  resourceIDs: string[] = [],
  isCreateTicket: boolean = false,
) => async (dispatch: Dispatch<RecipientsAction>, getState: Function) => {
  dispatch(getUsersStart());

  const onError = () => dispatch(getUsersError());

  const onSuccess = (usersResource: ContactsResource) => {
    const users = usersResource.contacts
      .map((item) => RecipientUtils.fromResource(item))
      .sort((firstRecipient: Recipient, secondRecipient: Recipient) =>
        firstRecipient.fullName
          .toLowerCase()
          .localeCompare(secondRecipient.fullName.toLowerCase()),
      );

    dispatch(getUsersSuccess(users));
  };

  return ApiRequest.post(
    `${CONTACTS_URL +
      (isCreateTicket ? '?includeDisabledNotifyContacts=true' : '')}`,
    { accountID, resourceIDs },
    { onError, onSuccess },
  );
};

const getRecipients = (ticketId: string) => async (
  dispatch: Dispatch<RecipientsAction>,
) => {
  dispatch(recipientsLoading());

  const onError = () => dispatch(recipientsError());

  const onSuccess = (response: TicketResource) => {
    const recipients = response.recipients.map((contact) =>
      RecipientUtils.fromResource(contact),
    );
    dispatch(setCurrentRecipients(recipients));
    dispatch(recipientsSuccess());
  };

  return ApiRequest.get(`${TICKETS_URL}/${ticketId}`, { onError, onSuccess });
};

const removeRecipientsFromTicket = () => async (
  dispatch: Dispatch<RecipientsAction>,
  getState: Function,
) => {
  dispatch(removeRecipientsStart());

  const { selectedTicket } = getState();
  const { recipients, ticketId } = selectedTicket;

  const newRecipients = RecipientUtils.withoutToRemove(recipients);

  const onError = () => dispatch(removeRecipientsError());

  const onSuccess = (response: CommentResponse) => {
    if (FeatureFlags.isActive('ticket-polling-on-update')) {
      TicketUtils.setPollingTicketList(
        selectedTicket.ticketId,
        response.eventId,
      );
      dispatch(setRecipientsUpdated(true));
      dispatch(setProcessingFlag(true));
      dispatch(startPolling());
    }
    dispatch(setCurrentRecipients(newRecipients));
    dispatch(removeRecipientsSuccess());
  };

  return ApiRequest.patch(
    `${TICKETS_URL}/${ticketId}`,
    { recipients: RecipientUtils.getIds(newRecipients) },
    {
      headers: {
        'x-async': 'true',
      },
      onError,
      onSuccess,
    },
  );
};

const submitNewRecipients = (
  recipients: Recipient[] | CloudGroupUser[],
) => async (dispatch: Dispatch<RecipientsAction>, getState: Function) => {
  dispatch(addRecipientStart());

  const { selectedTicket } = getState() as AppState;
  const currentRecipients = selectedTicket.recipients || [];

  let creator: Recipient | undefined;

  // Remove creator from current recipients so we don't send in the patch
  const result = currentRecipients.filter((recipient) => {
    if (!recipient._isCreator) {
      return recipient;
    }
    creator = recipient;
    return false;
  });

  const uniqueRecipients = RecipientUtils.unique(recipients.concat(result));

  const onError = () => dispatch(addRecipientsError());

  const onSuccess = (response: CommentResponse) => {
    // If creator exists put into front of list that will update the component.
    if (creator) {
      uniqueRecipients.unshift(creator);
    }
    if (FeatureFlags.isActive('ticket-polling-on-update')) {
      TicketUtils.setPollingTicketList(
        selectedTicket.ticketId,
        response.eventId,
      );
      dispatch(setRecipientsUpdated(true));
      dispatch(setProcessingFlag(true));
      dispatch(startPolling());
    }
    dispatch(setCurrentRecipients(uniqueRecipients));
    dispatch(addRecipientsSuccess());
  };

  return ApiRequest.patch(
    `${TICKETS_URL}/${selectedTicket.ticketId}`,
    { recipients: RecipientUtils.getIds(uniqueRecipients) },
    {
      headers: {
        'x-async': 'true',
      },
      onError,
      onSuccess,
    },
  );
};

const transformRecipients = (ticket: TicketResource) => async (
  dispatch: Dispatch<RecipientsAction>,
  getState: Function,
) => {
  /*
   * Wait for getUsers call so we can pass updated recipients.addUserItems
   * to RecipientUtils.transformRecipients
   */

  const deviceIds = TicketUtils.getResourceIds(ticket.resources);
  await dispatch(getUsers(ticket.accountId, deviceIds));

  const { accounts, recipients } = getState() as AppState;
  const ticketAccount = AccountUtils.findById(accounts.items, ticket.accountId);
  const isDedicated = AccountUtils.isDedicated(ticketAccount);

  const ticketRecipients = RecipientUtils.mapTicketRecipients(
    recipients.addUsersItems,
    ticket,
    isDedicated,
  );

  dispatch(setCurrentRecipients(ticketRecipients));
};

export {
  addNewUnverifiedInput,
  clearAllUnverifiedInputs,
  clearAllUserChecks,
  getRecipients,
  getUsers,
  getUsersSuccess,
  recipientsLoading,
  recipientsReducer,
  recipientsSuccess,
  removeNewUnverified,
  removeRecipientsError,
  removeRecipientsFromTicket,
  resetAddUser,
  setAddUserVisibility,
  setRecipientsFilterText,
  setUsersFilterText,
  submitNewRecipients,
  toggleUserCheck,
  transformRecipients,
  updateEmailInput,
};
